import { ArrowLeftIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TemplateSelector } from "../template-selector/TemplateSelector";

import { useImportFileController } from "./useImportFileController";
import { Separator } from "@/components/ui/separator";
import { FileSelector } from "../file-selector/FileSelector";
import { Show } from "@/components/core";
import { AddOrUpdateTemplate } from "../add-or-update-template/AddOrUpdateTemplate";
import { useEffect } from "react";
import { Tooltip } from "@radix-ui/react-tooltip";

export const ImportFile = () => {
  const navigate = useNavigate();
  const controller = useImportFileController();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");

  // Listen for tab switch and template selection events
  useEffect(() => {
    const handleSwitchTab = (event: CustomEvent) => {
      if (event.detail?.tab) {
        controller.setSelectedTab(event.detail.tab);
      }
    };

    const handleSelectTemplate = (event: CustomEvent) => {
      if (event.detail?.templateId) {
        controller.setSelectedTemplateId(event.detail.templateId);
      }
    };

    window.addEventListener("switchTab", handleSwitchTab as EventListener);
    window.addEventListener(
      "selectTemplate",
      handleSelectTemplate as EventListener
    );

    return () => {
      window.removeEventListener("switchTab", handleSwitchTab as EventListener);
      window.removeEventListener(
        "selectTemplate",
        handleSelectTemplate as EventListener
      );
    };
  }, [controller]);

  return (
    <div className="flex flex-col min-h-screen">
      <header className="border-b px-6 py-3 flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/app/imports${typeParam ? `?type=${typeParam}` : ''}`)}
          className="h-8 w-8 mr-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span className="sr-only">Back to Imports</span>
        </Button>

        <h1 className="text-xl font-bold">New Import</h1>
      </header>

      <main className="flex-1 container max-w-5xl py-6 px-4 md:px-6">
        <div className="grid gap-2">
          <div>
            <h2 className="text-lg font-semibold mb-2">1. Select Template</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Choose an existing template or create a new one for your import
            </p>
          </div>

          <Tabs
            value={controller.selectedTab}
            onValueChange={(value) => {
              controller.setSelectedTab(value);
              if (value === "new") {
                // Clear selection and file when creating a new template
                controller.setSelectedTemplateId(undefined);
                controller.setFile(undefined);
              }
            }}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existing">Use Existing Template</TabsTrigger>
              <TabsTrigger value="new">Create New Template</TabsTrigger>
            </TabsList>
            <TabsContent value="existing" className="mt-4">
              <TemplateSelector
                onSelectTemplate={controller.setSelectedTemplateId}
                selectedTemplateId={controller.selectedTemplateId}
              />
            </TabsContent>
            <TabsContent value="new" className="mt-4">
              <AddOrUpdateTemplate
                context={controller.context}
                templateId={controller.selectedTemplateId}
              />
            </TabsContent>
          </Tabs>

          <Separator className="my-4" />

          <FileSelector
            selectedTemplateId={controller.selectedTemplateId}
            onFileSelected={controller.setFile}
          />

          {/* start import */}

          <div className="flex justify-end gap-4 mt-4">
            <Button
              // disabled={
              //   controller.selectedTemplateId === undefined ||
              //   controller.file === undefined ||
              //   controller.selectedFund === undefined
              // }
              onClick={(e) => {
                e.preventDefault();
                controller.performImport();
              }}
            >
              Start Import
            </Button>
          </div>

          {/* Import Progress Overlay */}
          <Show if={controller.loading === true}>
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-card border rounded-lg shadow-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-semibold mb-4 text-center">
                  Uploading File for Import
                </h3>
              </div>
            </div>
          </Show>
        </div>
      </main>
    </div>
  );
};
